import React, { useState } from 'react';
import { getVocabulary } from '../utils/vocabulary.js';

/**
 * Frequency control shared by the amount step and the picker's cart summary.
 *
 * Display rules, all derived from Behaviour.Frequencies:
 * - one frequency → its label renders alone, already selected;
 * - exactly two → both render side by side in the segmented control, using
 *   their configured labels;
 * - more than two with OneTime → "{Termo} pontual | {Termo} recorrente"
 *   segmented control, with the recurring periods as pills while
 *   "recorrente" is active;
 * - more than two without OneTime → a static "{Termo} recorrente" segment
 *   with the period pills always visible.
 *
 * Renders nothing when the acquisition form offers a single frequency and the
 * caller asked to hide it (`hideWhenSingle`) — the cart summary has no reason
 * to show a control the donor cannot act on.
 */
export function FrequencySelector({ config, frequencyId, onChange, hideWhenSingle = false }) {
  const { Frequencies, DefaultFrequency } = config.Behaviour;
  const vocab = getVocabulary(config);

  const oneTime = Frequencies.find(f => f.Id === 'OneTime') || null;
  const recurring = Frequencies.filter(f => f.Id !== 'OneTime');

  const selectedFrequency = Frequencies.find(f => f.Id === frequencyId) || Frequencies[0];
  const isRecurringSelected = Boolean(selectedFrequency && selectedFrequency.Id !== 'OneTime');

  // The one-time → recurring toggle returns to the period the donor last picked.
  const defaultRecurringId = (recurring.find(f => f.Id === DefaultFrequency) || recurring[0])?.Id || null;
  const [lastRecurringId, setLastRecurringId] = useState(
    isRecurringSelected ? selectedFrequency.Id : defaultRecurringId
  );

  function handleChange(nextId) {
    if (!Frequencies.some(f => f.Id === nextId)) return;
    if (nextId !== 'OneTime') setLastRecurringId(nextId);
    onChange(nextId);
  }

  let segmentOptions = [];
  let showPills = false;

  if (Frequencies.length === 1 && selectedFrequency) {
    segmentOptions = [{ key: selectedFrequency.Id, label: selectedFrequency.Label, selected: true }];
  } else if (Frequencies.length === 2) {
    segmentOptions = Frequencies.map(freq => ({
      key: freq.Id,
      label: freq.Label,
      selected: frequencyId === freq.Id,
      onSelect: () => handleChange(freq.Id)
    }));
  } else if (Frequencies.length > 2 && oneTime) {
    segmentOptions = [
      {
        key: oneTime.Id,
        label: `${vocab.termCapitalized} pontual`,
        selected: !isRecurringSelected,
        onSelect: () => handleChange(oneTime.Id)
      },
      {
        key: 'recurring',
        label: `${vocab.termCapitalized} recorrente`,
        selected: isRecurringSelected,
        onSelect: () => handleChange(lastRecurringId)
      }
    ];
    showPills = isRecurringSelected;
  } else if (Frequencies.length > 2) {
    segmentOptions = [{ key: 'recurring', label: `${vocab.termCapitalized} recorrente`, selected: true }];
    showPills = true;
  }

  if (hideWhenSingle && Frequencies.length <= 1) return null;

  return (
    <>
      {segmentOptions.length > 0 && (
        <div className="tdw-freq-segment">
          {segmentOptions.map(option => option.onSelect ? (
            <button
              key={option.key}
              type="button"
              className={`tdw-freq-segment-option${option.selected ? ' selected' : ''}`}
              onClick={option.onSelect}
            >
              {option.label}
            </button>
          ) : (
            <span key={option.key} className="tdw-freq-segment-option selected">
              {option.label}
            </span>
          ))}
        </div>
      )}

      {showPills && (
        <div className="tdw-freq-pills">
          {recurring.map(freq => (
            <button
              key={freq.Id}
              type="button"
              className={`tdw-freq-pill${frequencyId === freq.Id ? ' selected' : ''}`}
              onClick={() => handleChange(freq.Id)}
            >
              {freq.Label}
            </button>
          ))}
        </div>
      )}
    </>
  );
}
